import { ComponentType, ReactElement } from 'react';
import { ServerData, ServerElementType } from './types';
import { render } from 'react-dom';
import { StrictMode } from 'react';

// Not possible to type props of Client Component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

type RenderArg = {
    additionalProps: ObjectType;
};

type RenderInstance = {
    node: Element;
    Component: AnyComponent;
    serverProps: unknown;
    serverRenderedChildren: ReactElement | null;
};

function ServerRenderedChildren({
    children,
    tagName,
}: {
    children: Element;
    tagName: 'div' | 'span';
}): ReactElement {
    const Tag = tagName;
    return (
        <Tag
            ref={(el?: HTMLElement | null) => {
                if (!el) {
                    return;
                }
                el.appendChild(children);
            }}
        />
    );
}

function isServerElementProp(prop: JsonType): prop is ServerElementType {
    return !Array.isArray(prop) && typeof prop === 'object' && prop?.__d_S_E === true;
}

export default class DibsClientRenderer {
    #namespace;
    #Wrapper: ComponentType;
    #components = new Map<string, AnyComponent>();
    #renderInstances: RenderInstance[] = [];
    constructor({ namespace, Wrapper }: { namespace: string; Wrapper: ComponentType }) {
        this.#namespace = namespace;
        this.#Wrapper = Wrapper;
    }
    registerComponent(componentName: string, Component: AnyComponent): void {
        this.#components.set(componentName, Component);
    }
    #transformProps = (props: JsonType, { additionalProps }: RenderArg): unknown => {
        const Wrapper = this.#Wrapper;
        if (typeof props !== 'object' || props === null) {
            return props;
        } else if (Array.isArray(props)) {
            return props.map(prop => this.#transformProps(prop, { additionalProps }));
        }
        const newProps: Record<string, unknown> = {};
        for (const [key, prop] of Object.entries(props)) {
            if (isServerElementProp(prop)) {
                const divContainer = document.createElement('div');
                divContainer.innerHTML = prop.__html;
                this.#renderEl(divContainer, { additionalProps });
                const serverChildContainer = divContainer.querySelector(
                    `[data-dibs-server-children]`
                );

                if (!serverChildContainer) {
                    throw new Error(
                        `No [data-dibs-server-children] inside 'useServerElement' prop, ${prop.__html}`
                    );
                }
                const tagName = serverChildContainer.tagName.toLowerCase() as 'div' | 'span';
                newProps[key] = (
                    <Wrapper>
                        <ServerRenderedChildren tagName={tagName} children={serverChildContainer} />
                    </Wrapper>
                );
            } else {
                newProps[key] = this.#transformProps(prop, { additionalProps });
            }
        }
        return newProps;
    };
    #renderEl = (el: Element, { additionalProps }: RenderArg): void => {
        const Wrapper = this.#Wrapper;
        const slots = el.querySelectorAll(`[data-dibs-server-component=${this.#namespace}]`);
        for (const slot of [...slots].reverse()) {
            const scriptTag = slot.querySelector('script');
            if (!scriptTag) {
                throw new Error(`no script tag found nested under ${slot}`);
            }
            let serverData: ServerData;
            try {
                serverData = JSON.parse(scriptTag.innerText);
            } catch (e) {
                throw new Error(
                    `Could not parse JSON from server component ${scriptTag.innerText}`
                );
            }
            const Component = this.#components.get(serverData.component);
            if (!Component) {
                throw new Error(
                    `No component ${serverData.component} registered under namespace ${
                        this.#namespace
                    }`
                );
            }

            const serverChildContainer = slot.querySelector(`[data-dibs-server-children]`);
            let serverRenderedChildren: ReactElement | null = null;

            if (serverChildContainer) {
                const tagName = serverChildContainer.tagName.toLowerCase() as 'div' | 'span';
                serverRenderedChildren = (
                    <ServerRenderedChildren tagName={tagName} children={serverChildContainer} />
                );
            }

            const serverProps = this.#transformProps(serverData.serverProps, { additionalProps });
            this.#renderInstances.push({
                node: slot,
                Component,
                serverProps,
                serverRenderedChildren,
            });
            render(
                <StrictMode>
                    <Wrapper>
                        <Component
                            {...serverProps}
                            {...additionalProps}
                            serverRenderedChildren={serverRenderedChildren}
                        />
                    </Wrapper>
                </StrictMode>,
                slot
            );
        }
    };
    render({ additionalProps }: RenderArg): void {
        this.#renderEl(document.body, { additionalProps });
    }
    rerender({ additionalProps }: RenderArg): void {
        const Wrapper = this.#Wrapper;
        for (const { node, Component, serverProps, serverRenderedChildren } of this
            .#renderInstances) {
            render(
                <StrictMode>
                    <Wrapper>
                        <Component
                            {...serverProps}
                            {...additionalProps}
                            serverRenderedChildren={serverRenderedChildren}
                        />
                    </Wrapper>
                </StrictMode>,
                node
            );
        }
    }
}
